import {
    ChatBubbleOutlineOutlined,
    ConfirmationNumber,
    DeleteOutlineOutlined,
    DescriptionOutlined,
    Download,
    FolderOpenOutlined,
    InfoOutlined,
    InsertDriveFileOutlined,
} from '@mui/icons-material';
import {
    Badge,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';

import DocumentStatusChip from './documentStatusChip';
import UploadDocument from './uploadDocument';
import Result from 'components/v2/common/Result';

import { AppDocument } from 'api';
import { DocStatus } from 'data/constants/DocStatus';
import useTranslate from 'hooks/useTranslate';

interface DocumentCardViewProps {
    documents: AppDocument[];
    uploadingDocId: number | null;
    onView: (doc: AppDocument) => void;
    onUpload: (doc: AppDocument, file: File) => void;
    onDownload: (doc: AppDocument) => void;
    onDelete: (doc: AppDocument) => void;
    onChangeStatus: (doc: AppDocument, status: DocStatus) => void;
    onPreviewDocumentFile?: (doc: AppDocument, previewType: 'sample' | 'template') => void;
    openTicketCounts?: Record<number, number>;
    onViewTickets?: (doc: AppDocument) => void;
    onChat?: (doc: AppDocument) => void;
    onShowDescription?: (doc: AppDocument) => void;
}

const DocumentCardView = ({
    documents,
    uploadingDocId,
    onView,
    onUpload,
    onDownload,
    onDelete,
    onChangeStatus,
    onPreviewDocumentFile,
    openTicketCounts = {},
    onViewTickets,
    onChat,
    onShowDescription,
}: DocumentCardViewProps) => {
    const { translate } = useTranslate();

    if (!documents.length) {
        return (
            <Result
                icon={<FolderOpenOutlined sx={{ fontSize: 32 }} />}
                title={translate('noMatchingDocuments')}
                description={translate('noMatchingDocumentsDescription')}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                },
                gap: 2,
            }}
        >
            {documents.map((doc) => {
                const ticketCount = openTicketCounts[doc.id] ?? 0;
                const hasFile = Boolean(doc.resource_name);

                return (
                    <Card
                        key={doc.id}
                        variant="outlined"
                        sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                        <CardContent sx={{ flex: 1, pb: 1 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ minWidth: 0, alignItems: 'center', flex: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        onClick={() => {
                                            if (hasFile) onView(doc);
                                        }}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            fontWeight: hasFile ? 700 : 400,
                                            color: hasFile ? 'primary.main' : 'text.secondary',
                                            cursor: hasFile ? 'pointer' : 'default',
                                        }}
                                    >
                                        {doc.document_name}
                                    </Typography>
                                    <Tooltip title={translate('description')}>
                                        <IconButton
                                            size="small"
                                            sx={{ flexShrink: 0 }}
                                            onClick={() => onShowDescription?.(doc)}
                                        >
                                            <InfoOutlined
                                                sx={{ fontSize: 16, color: 'text.disabled' }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <DocumentStatusChip
                                    status={doc.status}
                                    onChangeStatus={(newStatus) => onChangeStatus(doc, newStatus)}
                                />
                            </Stack>
                        </CardContent>

                        <Divider />

                        <CardActions
                            sx={{
                                justifyContent: ticketCount > 0 ? 'space-between' : 'flex-end',
                                px: 1.5,
                                py: 0.75,
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            {ticketCount > 0 ? (
                                <Badge badgeContent={ticketCount} color="warning">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={
                                            <ConfirmationNumber
                                                sx={{ fontSize: 14, color: 'warning.main' }}
                                            />
                                        }
                                        onClick={() => onViewTickets?.(doc)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {translate('viewTickets')}
                                    </Button>
                                </Badge>
                            ) : null}
                            <Stack direction="row" spacing={0.25}>
                                {doc.sample_uri ? (
                                    <Tooltip title={translate('previewSample')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onPreviewDocumentFile?.(doc, 'sample')}
                                        >
                                            <DescriptionOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                {doc.template_uri ? (
                                    <Tooltip title={translate('previewTemplate')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onPreviewDocumentFile?.(doc, 'template')}
                                        >
                                            <InsertDriveFileOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title={translate('documentChat')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!hasFile}
                                            onClick={() => onChat?.(doc)}
                                        >
                                            <ChatBubbleOutlineOutlined fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <UploadDocument
                                    onUpload={(file) => onUpload(doc, file)}
                                    loading={uploadingDocId === doc.id}
                                />
                                <Tooltip title={translate('documentsDownload')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!hasFile}
                                            onClick={() => onDownload(doc)}
                                        >
                                            <Download fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={translate('deleteDocument')}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => onDelete(doc)}
                                    >
                                        <DeleteOutlineOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </CardActions>
                    </Card>
                );
            })}
        </Box>
    );
};

export default DocumentCardView;
